import React, { useEffect, useState } from "react";
import { classNames } from "../../../../helpers/classNames";
import Modal from "../../../elements/Modal";
import Indentification from "../../../elements/Indentification";
import {
  initialsValue,
  loadDataOnScroll,
  replaceOldData,
} from "../../../../helpers";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  organizationAddEdit,
  organizationDetails,
} from "../../../../services/organizationService";
import {
  profileDetails,
  profileUpdate,
} from "../../../../services/ProfileService";
import { lngDetect } from "../../../../helpers/lngDetect";
import useForm from "../../../../hooks/useForm";
import { useDebounce } from "../../../../hooks/useDebounce";
import { countryList } from "../../../../services/countryService";
import Form from "../../../form/Form";
import { useDispatch } from "react-redux";
import { setSession } from "../../../../redux/slice/authSlice";
import { useSelector } from "react-redux";
import Button from "../../../form/Button";
import AddressDetails from "./AddressDetails";
import SocialDetails from "./SocialDetails";
import Textarea from "../../../form/Textarea";
import { t } from "i18next";

const socialMedia = [
  {
    url: "",
    name: "Facebook",
    splitFrom: ["facebook.com/"],
    icon: "fa-brands fa-facebook-f",
  },
  {
    url: "",
    name: "X",
    splitFrom: ["x.com/"],
    icon: "fa-brands fa-x-twitter",
  },
  {
    url: "",
    name: "Instagram",
    splitFrom: ["instagram.com/"],
    icon: "fa-brands fa-instagram",
  },
  {
    url: "",
    name: "YouTube",
    splitFrom: ["youtube.com/", "youtu.be/"],
    icon: "fa-brands fa-youtube",
  },
];

const ProfileDetailsModal = ({ selectedProfile = {} }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [countryFilter, setCountryFilter] = useState(null);
  const [countryKeyword, setCountryKeyword] = useState("");
  const countrySearch = useDebounce(countryKeyword, 300);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const { lang } = useSelector((state) => state?.langProvider);
  const { values, handleChange, handleSubmit, errors, setFieldsValue } =
    useForm({
      initialState: {
        socialMedia,
      },
    });

  const {
    data: profileDetailsData,
    isLoading: profileDataLoading,
    isFetching: profileDataFetching,
  } = useQuery({
    queryKey: ["selected-profile-details", selectedProfile],
    enabled: !!selectedProfile?.value,
    queryFn: async () => {
      let res = null;

      if (selectedProfile?.description === "profile") {
        res = await profileDetails();
      } else {
        res = await organizationDetails({
          id: selectedProfile?.value,
        });

        if (res?.status === 200) {
          const normalizedStyles = Object.keys(
            res?.data?.themeSetting?.styles || {}
          ).reduce((acc, key) => {
            const val = res.data.themeSetting.styles[key];
            acc[key] = val == null ? [] : Array.isArray(val) ? val : [val];
            return acc;
          }, {});

          res = {
            ...res,
            data: {
              ...res?.data,
              themeSetting: {
                ...res?.data?.themeSetting,
                styles: normalizedStyles,
              },
            },
          };
        } else {
          toast.warning(res?.message);
        }
      }
      if (res?.status === 200) {
        let basic_info = {};
        if (selectedProfile?.description === "profile") {
          basic_info = {
            _id: res?.data?._id,
            firstName: res?.data?.firstName,
            lastName: res?.data?.lastName,
            image: res?.data?.image?.url,
            email: res?.data?.email,
            role: res?.data?.role,
            phone: res?.data?.phone?.fullNumber,
            address: res?.data?.address?.[0] || {},
          };
        } else {
          basic_info = {
            _id: res?.data?._id,
            firstName: lngDetect(res?.data?.name),
            lastName: "",
            image: res?.data?.logo?.url,
            email: res?.data?.contactEmail,
            role: "organization",
            phone: res?.data?.phone?.fullNumber,
            description: res?.data?.description,
            address: res?.data?.address || {},
            socialMedia: res?.data?.setting?.socialMedia?.map((item) => {
              const find_social = socialMedia?.find(
                (social) => social?.name === item?.name
              );
              if (item?.name === find_social?.name) {
                return { ...item, splitFrom: find_social?.splitFrom };
              } else {
                return item;
              }
            }),
          };
        }

        return { ...basic_info };
      } else {
        return {};
      }
    },
  });

  const { data: countryData } = useQuery({
    queryKey: ["country", countryFilter, countrySearch],
    queryFn: async () => {
      const res = await countryList({
        ...countryFilter,
        ...(countrySearch && { keyword: countrySearch }),
      });

      if (countryFilter?.page > 1 && !countrySearch) {
        const marge_arr = [...(countryData?.data || []), ...res?.docs?.data];
        return {
          ...res?.docs,
          data: replaceOldData({
            oldData: marge_arr,
            newData: res?.docs?.data,
          }),
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (profileDetailsData?._id) {
      setFieldsValue({
        phone: profileDetailsData?.phone,
        firstName: profileDetailsData?.firstName,
        lastName: profileDetailsData?.lastName,
        email: profileDetailsData?.email,
        image: profileDetailsData?.image,
        address1: profileDetailsData?.address?.address1,
        address2: profileDetailsData?.address?.address2,
        city: profileDetailsData?.address?.city,
        zipcode: profileDetailsData?.address?.zipcode,
        state: profileDetailsData?.address?.state,
        country: profileDetailsData?.address?.country,
        description: lngDetect(profileDetailsData?.description),
        socialMedia:
          profileDetailsData?.socialMedia?.length > 0
            ? profileDetailsData?.socialMedia
            : socialMedia,
      });
    }
  }, [profileDetailsData]);

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (value) => {
      const address = [
        {
          address1: value?.address1,
          address2: value?.address2,
          city: value?.city,
          zipcode: value?.zipcode,
          state: value?.state,
          country: value?.country,
        },
      ];
      if (selectedProfile?.description === "profile") {
        const payload = {
          ...value,
          address,
        };
        delete payload?.address1;
        delete payload?.address2;
        delete payload?.city;
        delete payload?.zipcode;
        delete payload?.state;
        delete payload?.country;
        return await profileUpdate(payload);
      } else {
        const payload = {
          description: { [lang]: value?.description },
          address: address?.[0] || {},
          setting: {
            socialMedia: value?.socialMedia,
          },
        };

        delete payload?.logo;
        delete payload?.socialMedia;

        const res = await organizationAddEdit({
          id: selectedProfile?.value,
          ...payload,
        });
        return res;
      }
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        if (selectedProfile?.description === "profile") {
          queryClient.setQueryData(["profile"], (state) => {
            const state_data = JSON.parse(JSON.stringify(state || {}));
            state_data.data = res?.data;
            return { ...state_data };
          });
          dispatch(
            setSession({
              user: { ...res?.data },
              status: "authorized",
              error: null,
            })
          );
        }
        queryClient.invalidateQueries({
          queryKey: ["selected-profile-details", selectedProfile],
        });
      } else {
        toast.error(res?.message || res?.error);
      }
    },
  });

  return (
    <>
      {selectedProfile?.description === "organization" && (
        <button
          onClick={() => {
            setModalOpen({ isOpen: true, data: null });
          }}
          className={classNames(
            "p-2 w-full block bg-purple-100 text-start rounded-lg text-sm hover:shadow-lg transition-all duration-300 border border-purple-300 cursor-pointer relative",
            "before:content-[''] before:absolute before:!bg-purple-100 before:w-4 before:h-4 before:left-4 before:-top-1 before:shadow-lg before:rotate-45 before:-z-[1] before:border before:border-slate-300/50"
          )}
        >
          <div className="line-clamp-2">
            {lngDetect(profileDetailsData?.description)}
          </div>
        </button>
      )}
      <div
        onClick={() => {
          setModalOpen({ isOpen: true });
        }}
        className="relative bg-white rounded-xl  py-4 px-4 space-y-2 shadow"
      >
        <div className="text-sm text-gray-700">
          <ul className="space-y-1">
            <li className="grid grid-cols-12 gap-2 cursor-pointer transition-all duration-300">
              <div className="col-span-1 flex pt-[2px] justify-center">
                <i className="fa-regular fa-envelope"></i>
              </div>
              <div className="col-span-11">{profileDetailsData?.email}</div>
            </li>
            {profileDetailsData?.socialMedia?.map((social) => {
              // find the matching pattern to split from
              const splitKey = social.splitFrom.find((key) =>
                social.url.includes(key)
              );
              const username = splitKey
                ? social.url.split(splitKey).at(-1)
                : social.url;

              return (
                <li key={social?.name} className="grid grid-cols-12 gap-2">
                  <div className="col-span-1 flex pt-[2px] justify-center">
                    <i className={classNames(social?.icon)}></i>
                  </div>
                  <div className="col-span-11">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`https://${splitKey}${username}`}
                      className="text-gray-700"
                    >
                      {username}
                    </a>
                  </div>
                </li>
              );
            })}
            <li className="grid grid-cols-12 gap-2 cursor-pointer transition-all duration-300">
              <div className="col-span-1 flex pt-[2px] justify-center">
                <i className="fa-regular fa-location-dot"></i>
              </div>
              <div className="col-span-11">
                {Object.keys(profileDetailsData?.address || {})
                  ?.filter((key) =>
                    [
                      "country",
                      "city",
                      "state",
                      "zipcode",
                      "address1",
                      "address2",
                    ].includes(key)
                  )
                  ?.map((key) => profileDetailsData?.address[key])
                  .join(", ") || "-"}
              </div>
            </li>
          </ul>
        </div>
        <div className="flex">
          <Button
            buttonLabel={t("See About Info")}
            buttonClasses={"!h-8 !px-0 !bg-none !text-purple-heart"}
            buttonLabelClasses={"!capitalize !font-normal"}
          />
        </div>
      </div>
      <Modal
        isOpen={modalOpen?.isOpen}
        setIsOpen={() => {
          setModalOpen({ isOpen: false, data: null });
        }}
      >
        <Form handleSubmit={handleSubmit} onSubmit={onSubmit}>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-20 aspect-square rounded-full overflow-hidden border-1 border-slate-300 flex-shrink-0 bg-white">
                <Indentification
                  image={profileDetailsData?.image}
                  alt={
                    profileDetailsData?.image ||
                    `${profileDetailsData?.firstName} ${profileDetailsData?.lastName}`
                  }
                  initial={initialsValue(
                    `${profileDetailsData?.firstName} ${profileDetailsData?.lastName}`
                  )}
                  className={"overflow-hidden"}
                  size="max"
                  initialClass={"text-[25px] font-semibold"}
                />
              </div>
              <div>
                <h3 className="text-xl font-semibold">
                  {`${profileDetailsData?.firstName} ${profileDetailsData?.lastName}`}
                </h3>
                <div className="text-text-disabled">
                  {profileDetailsData?.role}
                </div>
              </div>
            </div>
            {selectedProfile?.description === "organization" && (
              <div className="space-y-2">
                <div className="text-xl lg:text-2xl text-deep-purple font-bold font-Montserrat">
                  {t("About")}
                </div>
                <Textarea
                  inputValue={values?.description}
                  onChange={handleChange}
                  inputName={"description"}
                />
              </div>
            )}
            <AddressDetails
              values={values}
              handleChange={handleChange}
              loadDataOnScroll={loadDataOnScroll}
              profileDetailsData={profileDetailsData}
              countryData={countryData}
              setCountryKeyword={setCountryKeyword}
              setCountryFilter={setCountryFilter}
              countryKeyword={countryKeyword}
            />
            {selectedProfile?.description !== "profile" && (
              <SocialDetails
                selectedProfile={selectedProfile}
                values={values}
                setFieldsValue={setFieldsValue}
              />
            )}
          </div>
          <Button
            {...((isPending || profileDataLoading || profileDataFetching) && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
            buttonType="submit"
            buttonLabel={t("Save profile")}
            buttonClasses={"ml-auto"}
          />
        </Form>
      </Modal>
    </>
  );
};

export default ProfileDetailsModal;
